import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
  Typography,
  Divider,
  SelectChangeEvent,
  IconButton,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Pagination
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Clear as ClearIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

let cachedDepartments: string[] | null = null;
let departmentsFetchPromise: Promise<string[]> | null = null;

const fetchDepartmentsOnce = async (getAccessToken: () => Promise<string | null>): Promise<string[]> => {
  if (cachedDepartments) {
    return cachedDepartments;
  }

  if (!departmentsFetchPromise) {
    departmentsFetchPromise = (async () => {
      try {
        const token = await getAccessToken();
        if (!token) {
          return [];
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:9110'}/api/proposal/departments`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch departments: ${response.status}`);
        }

        const depts = await response.json();
        cachedDepartments = Array.isArray(depts) ? depts : [];
        return cachedDepartments;
      } catch (error) {
        console.error('Failed to fetch departments:', error);
        throw error;
      } finally {
        departmentsFetchPromise = null;
      }
    })();
  }

  try {
    const result = await departmentsFetchPromise;
    cachedDepartments = result;
    return result;
  } catch (error) {
    // Ensure future callers can retry in case of failure
    departmentsFetchPromise = null;
    throw error;
  }
};

// Simple debounce implementation
const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export interface User {
  id: string;
  displayName: string;
  mail: string;
  jobTitle?: string;
  department?: string;
  officeLocation?: string;
  employmentType?: 'fullTime' | 'contractor';
  hasProfile?: boolean;
  profileLastUpdated?: string;
}

interface FilterConfig {
  value?: any;
  visible?: boolean;
  locked?: boolean;
}

interface UserBrowserProps {
  filters?: {
    hasProfile?: boolean | FilterConfig;
    department?: string | FilterConfig;
    employmentType?: 'fullTime' | 'contractor' | 'all' | FilterConfig;
  };
  multiSelect?: boolean;
  onSelectionChange: (users: User[]) => void;
  initialSelection?: User[];
  maxSelections?: number;
  itemsPerPage?: number;
}

const UserBrowser: React.FC<UserBrowserProps> = ({
  filters = {},
  multiSelect = true,
  onSelectionChange,
  initialSelection,
  maxSelections,
  itemsPerPage = 10
}) => {
  const { getAccessToken } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<User[]>(initialSelection || []);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [departments, setDepartments] = useState<string[]>([]);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Parse filter configurations
  const parseFilterConfig = (filter: any): FilterConfig => {
    if (filter === undefined || filter === null) {
      return { value: undefined, visible: true, locked: false };
    }
    if (typeof filter === 'object' && 'value' in filter) {
      return {
        value: filter.value,
        visible: filter.visible !== false,
        locked: filter.locked === true
      };
    }
    return { value: filter, visible: true, locked: false };
  };

  const hasProfileConfig = parseFilterConfig(filters.hasProfile);
  const departmentConfig = parseFilterConfig(filters.department);
  const employmentTypeConfig = parseFilterConfig(filters.employmentType);

  // Initialize local filters with parsed values
  const [localFilters, setLocalFilters] = useState({
    hasProfile: hasProfileConfig.value,
    department: departmentConfig.value,
    employmentType: employmentTypeConfig.value || 'all'
  });

  // Count active filters for badge
  const activeFilterCount = [
    localFilters.hasProfile && hasProfileConfig.visible !== false ? 1 : 0,
    localFilters.department && departmentConfig.visible !== false ? 1 : 0,
    localFilters.employmentType && localFilters.employmentType !== 'all' && employmentTypeConfig.visible !== false ? 1 : 0
  ].reduce((sum, val) => sum + val, 0);

  // Sync selectedUsers when initialSelection changes
  useEffect(() => {
    if (initialSelection) {
      setSelectedUsers(initialSelection);
    }
  }, [initialSelection]);

  // Fetch departments on mount
  useEffect(() => {
    if (!filtersExpanded || departments.length > 0) {
      return;
    }

    let isMounted = true;

    fetchDepartmentsOnce(getAccessToken)
      .then((depts) => {
        console.log('[UserBrowser] Departments fetched', depts?.length ?? 0);
        if (isMounted) {
          setDepartments(depts);
        }
      })
      .catch(() => {
        console.warn('[UserBrowser] Departments fetch failed');
        if (isMounted) {
          setDepartments([]);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [filtersExpanded, departments.length, getAccessToken]);

  // Main search function - single API call with all parameters
  const searchUsers = useCallback(async (
    query: string,
    page: number,
    filters: any
  ) => {
    const overallStart = performance.now();
    // Don't search if query is too short (except wildcards)
    if (!query || (query !== '*' && query.length < 2 && !query.includes('*'))) {
      setUsers([]);
      setTotalCount(0);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Calculate offset for backend pagination
      const offset = (page - 1) * itemsPerPage;

      // Build query parameters
      const params = new URLSearchParams({
        q: query,
        limit: String(itemsPerPage),
        offset: String(offset)
      });

      // Add filters to query params
      if (filters.hasProfile !== undefined && filters.hasProfile !== null) {
        params.append('has_profile', String(filters.hasProfile));
      }
      if (filters.department && filters.department !== '') {
        params.append('department', filters.department);
      }
      if (filters.employmentType && filters.employmentType !== 'all') {
        params.append('employment_type', filters.employmentType);
      }

      const tokenStart = performance.now();
      const token = await getAccessToken();
      const tokenDuration = performance.now() - tokenStart;
      if (process.env.NODE_ENV === 'development') {
        console.log('[UserBrowser] getAccessToken duration', Math.round(tokenDuration), 'ms for query', query);
      }
      if (!token) {
        throw new Error('No authentication token available');
      }

     const fetchStart = performance.now();
      const requestUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:9110'}/api/proposal/users/search?${params}`;
      if (process.env.NODE_ENV === 'development') {
        console.log('[UserBrowser] fetching users from', requestUrl);
      }
      const response = await fetch(
        requestUrl,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (process.env.NODE_ENV === 'development') {
        console.log('[UserBrowser] fetch users duration', Math.round(performance.now() - fetchStart), 'ms for query', query);
      }

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();

      // Map backend response to User interface
      const mappedUsers = (data.items || []).map((u: any) => ({
        id: u.user_id,
        displayName: u.display_name,
        mail: u.email,
        jobTitle: u.job_title,
        department: u.department,
        hasProfile: u.has_profile
      }));

      setUsers(mappedUsers);
      setTotalCount(data.total || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching users:', err);
      setUsers([]);
      setTotalCount(0);
    } finally {
      if (process.env.NODE_ENV === 'development') {
        console.log('[UserBrowser] searchUsers total duration', Math.round(performance.now() - overallStart), 'ms for query', query);
      }
      setLoading(false);
    }
  }, [getAccessToken, itemsPerPage]);

  // Debounced search function
  const debouncedSearch = useMemo(
    () => debounce((query: string, page: number, filters: any) => {
      searchUsers(query, page, filters);
    }, 300),
    [searchUsers]
  );

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    setError(null);
    setCurrentPage(1); // Reset to first page on new search

    // Trigger debounced search
    debouncedSearch(value || '*', 1, localFilters);
  };

  // Handle filter changes
  const handleFilterChange = (filterType: string, value: any) => {
    // Check if filter is locked
    const configs: Record<string, FilterConfig> = {
      hasProfile: hasProfileConfig,
      department: departmentConfig,
      employmentType: employmentTypeConfig
    };

    if (configs[filterType]?.locked) {
      return; // Don't allow changes to locked filters
    }

    const newFilters = { ...localFilters, [filterType]: value };
    setLocalFilters(newFilters);
    setCurrentPage(1); // Reset to first page on filter change

    // Re-search with new filters
    if (searchTerm || searchTerm === '') {
      searchUsers(searchTerm || '*', 1, newFilters);
    }
  };

  // Handle page change
  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
    searchUsers(searchTerm || '*', value, localFilters);
  };

  // Handle user selection
  const handleUserToggle = (user: User) => {
    let newSelection: User[];

    if (multiSelect) {
      const isSelected = selectedUsers.some(u => u.id === user.id);
      if (isSelected) {
        newSelection = selectedUsers.filter(u => u.id !== user.id);
      } else {
        if (maxSelections && selectedUsers.length >= maxSelections) {
          setError(`Maximum ${maxSelections} users can be selected`);
          return;
        }
        newSelection = [...selectedUsers, user];
      }
    } else {
      newSelection = [user];
    }

    setSelectedUsers(newSelection);
    onSelectionChange(newSelection);
  };

  // Handle clear selection
  const handleClearSelection = () => {
    setSelectedUsers([]);
    onSelectionChange([]);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
    setUsers([]);
    setTotalCount(0);
    setCurrentPage(1);
  };

  // Check if user is selected
  const isUserSelected = (user: User) => selectedUsers.some(u => u.id === user.id);

  // Calculate pagination info
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const showingFrom = totalCount > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0;
  const showingTo = Math.min(currentPage * itemsPerPage, totalCount);

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      gap: 2
    }}>
      {/* Header Section */}
      <Box>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom color="text.primary">
          Select Team Members
        </Typography>

        {/* Filters Accordion - Only show if any filters are visible */}
        {(hasProfileConfig.visible || departmentConfig.visible || employmentTypeConfig.visible) && (
          <Accordion
            expanded={filtersExpanded}
            onChange={(_, expanded) => setFiltersExpanded(expanded)}
            sx={{
              mb: 1,
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 'divider',
              '&:before': { display: 'none' },
              backgroundColor: 'rgba(79, 172, 254, 0.02)'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: 36,
                '& .MuiAccordionSummary-content': {
                  margin: '8px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterListIcon fontSize="small" />
                <Typography variant="body2">
                  Filters{activeFilterCount > 0 && ` (${activeFilterCount})`}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {hasProfileConfig.visible && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={localFilters.hasProfile || false}
                        onChange={(e) => handleFilterChange('hasProfile', e.target.checked)}
                        disabled={hasProfileConfig.locked}
                      />
                    }
                    label={
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Has Profile
                        {hasProfileConfig.locked && (
                          <Chip label="Locked" size="small" variant="outlined" sx={{ ml: 1 }} />
                        )}
                      </Box>
                    }
                  />
                )}

                {departmentConfig.visible && (
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Department</InputLabel>
                    <Select
                      value={localFilters.department || ''}
                      label="Department"
                      onChange={(e: SelectChangeEvent) => handleFilterChange('department', e.target.value)}
                      disabled={departmentConfig.locked}
                    >
                      <MenuItem value="">All</MenuItem>
                      {departments.map(dept => (
                        <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {employmentTypeConfig.visible && (
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Employment Type</InputLabel>
                    <Select
                      value={localFilters.employmentType || 'all'}
                      label="Employment Type"
                      onChange={(e: SelectChangeEvent) => handleFilterChange('employmentType', e.target.value)}
                      disabled={employmentTypeConfig.locked}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="fullTime">Full Time</MenuItem>
                      <MenuItem value="contractor">Contractor</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Search Field */}
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Search users... (use * for all users)"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{
            mb: 1,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(79, 172, 254, 0.02)',
              '&:hover': {
                backgroundColor: 'rgba(79, 172, 254, 0.05)',
              },
              '&.Mui-focused': {
                backgroundColor: 'white',
              }
            }
          }}
        />
      </Box>

      <Divider />

      {/* Error Display */}
      {error && (
        <Alert
          severity="error"
          sx={{ py: 0.5, px: 1, mb: 1 }}
          onClose={() => setError(null)}
        >
          <Typography variant="caption">{error}</Typography>
        </Alert>
      )}

      {/* User List */}
      <Box sx={{
        flexGrow: 1,
        overflow: 'auto',
        minHeight: 0
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : users.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ py: 4 }}
          >
            {!searchTerm ? 'Enter a search term or use * to see all users' :
             searchTerm.length === 1 ? 'Enter at least 2 characters to search' :
             'No users found'}
          </Typography>
        ) : (
          <>
            <List dense sx={{ py: 0, bgcolor: 'transparent' }}>
              {users.map(user => (
                <ListItem
                  key={user.id}
                  onClick={() => handleUserToggle(user)}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: 1,
                    mb: 0.25,
                    py: 0.75,
                    backgroundColor: isUserSelected(user) ? 'rgba(79, 172, 254, 0.1)' : 'rgba(255, 255, 255, 0.5)',
                    border: '1px solid',
                    borderColor: isUserSelected(user) ? 'primary.light' : 'transparent',
                    '&:hover': {
                      backgroundColor: isUserSelected(user)
                        ? 'rgba(79, 172, 254, 0.15)'
                        : 'rgba(0, 0, 0, 0.02)',
                      borderColor: isUserSelected(user) ? 'primary.main' : 'divider'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {multiSelect ? (
                      isUserSelected(user) ?
                        <CheckBoxIcon color="primary" fontSize="small" /> :
                        <CheckBoxOutlineBlankIcon fontSize="small" />
                    ) : (
                      <PersonIcon fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" noWrap sx={{ flex: 1, color: 'text.primary' }}>
                          {user.displayName}
                        </Typography>
                        {user.hasProfile && (
                          <Chip
                            label="✓"
                            size="small"
                            sx={{
                              ml: 0.5,
                              height: '16px',
                              fontSize: '10px',
                              bgcolor: 'rgba(76, 175, 80, 0.15)',
                              color: 'success.dark',
                              border: '1px solid',
                              borderColor: 'success.light'
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {user.jobTitle || 'No title'}
                        {user.department && ` • ${user.department}`}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mt: 2,
                gap: 1
              }}>
                <Typography variant="caption" color="text.secondary">
                  Showing {showingFrom}-{showingTo} of {totalCount} users
                </Typography>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  size="small"
                  color="primary"
                  siblingCount={0}
                  boundaryCount={1}
                />
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Selected Users Summary */}
      {selectedUsers.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1
          }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {selectedUsers.length} selected
              {maxSelections && ` / ${maxSelections} max`}
            </Typography>
            {multiSelect && (
              <Button
                size="small"
                variant="text"
                color="error"
                onClick={handleClearSelection}
                sx={{ minWidth: 'auto', p: 0.5 }}
              >
                Clear all
              </Button>
            )}
          </Box>

          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            maxHeight: '120px',
            overflowY: 'auto',
            p: 1,
            bgcolor: 'rgba(79, 172, 254, 0.02)',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1
          }}>
            {selectedUsers.map(user => {
              const initials = user.displayName.split(' ')
                .map(n => n[0])
                .slice(0, 2)
                .join('')
                .toUpperCase();

              return (
                <Chip
                  key={user.id}
                  label={initials}
                  size="small"
                  onDelete={() => handleUserToggle(user)}
                  deleteIcon={<ClearIcon />}
                  title={user.displayName}
                  sx={{
                    bgcolor: user.hasProfile ? 'rgba(76, 175, 80, 0.15)' : 'rgba(255, 152, 0, 0.15)',
                    color: user.hasProfile ? 'success.dark' : 'warning.dark',
                    border: '1px solid',
                    borderColor: user.hasProfile ? 'success.light' : 'warning.light',
                    '& .MuiChip-deleteIcon': {
                      fontSize: '16px',
                      color: 'inherit',
                      opacity: 0.7,
                      '&:hover': {
                        opacity: 1
                      }
                    }
                  }}
                />
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default UserBrowser;
